<template>
  <div>
    <spinner-loader :loadingStuff="loadingStuff" />
    <app-template />
    <div
      class="uk-container uk-container-large container settings-page with-drawer"
      :class="{ 'nav-collapsed': navCollapsed }"
    >
      <div class="intro-header">
        <div class="sessons-header">
          <div class="form">
            <div class="header-buttons">
              <h2>Settings</h2>
            </div>
          </div>
        </div>
      </div>
      <div class="settings-body">
        <SectionNav
          title="Settings"
          :items="settingsNavItems"
          :active="selectedNav"
          v-model:collapsed="navCollapsed"
          @select="(k) => changeSubNav(k)"
        />
        <div class="settings-content">
          <template v-if="selectedNav === 'teams'">
            <div class="settings-wrapper">
              <h2>Teams</h2>
              <button
                v-if="userData.userType === 'Admin'"
                @click="openAddNewTeamModal()"
                class="green-button"
              >
                Add new team
              </button>

              <p>Total number of teams: {{ teams.length }}</p>
              <div v-if="teams.length > 0" class="">
                <vue-good-table
                  style-class="vgt-table bordered"
                  :columns="columnsTeams"
                  :rows="teams"
                  :search-options="{
                    enabled: true,
                    placeholder: 'Search teams',
                  }"
                >
                  <template v-slot:table-row="props">
                    <span
                      v-if="
                        props.column.field == 'season_start_date' ||
                        props.column.field == 'season_end_date'
                      "
                    >
                      <span v-if="props.formattedRow[props.column.field]">{{
                        formatDates(props.formattedRow[props.column.field])
                      }}</span>
                      <span
                        v-else
                        class="inherits-text"
                        :title="'Inherits club default'"
                        >—</span
                      >
                      <span
                        v-if="
                          props.column.field == 'season_start_date' &&
                          (props.row.season_start_date ||
                            props.row.season_end_date)
                        "
                        class="override-pill"
                        title="This team has its own dates instead of the club default"
                        >Override</span
                      >
                    </span>
                    <span
                      class="action-buttons"
                      v-else-if="props.column.field == 'actions'"
                    >
                      <b-button
                        @click="openEditTeamModal(props.row)"
                        variant="outline-warning"
                        v-b-tooltip.hover
                        title="Edit"
                        ><i class="bi bi-pencil" aria-hidden="true"></i
                      ></b-button>
                      <b-button
                        @click="openSeasonDatesEditor(props.row)"
                        variant="outline-info"
                        v-b-tooltip.hover
                        title="Season dates"
                        ><i class="bi bi-calendar-range" aria-hidden="true"></i
                      ></b-button>
                      <b-button
                        @click="veiwTeam(props.row)"
                        variant="outline-success"
                        v-b-tooltip.hover
                        title="View team"
                        ><i class="bi bi-eye" aria-hidden="true"></i
                      ></b-button>
                    </span>
                    <span v-else-if="props.column.field == 'teamIcon'">
                      <TenantLogo
                        logo-class="login-logo"
                        class="user-image-small"
                      />
                    </span>
                  </template>
                </vue-good-table>
              </div>
            </div>
          </template>

          <template v-if="selectedNav === 'archivedPlayers'">
            <div class="settings-wrapper">
              <h2>Archived Players</h2>
            </div>

            <p>Total number of players: {{ allArchivedPlayersData.length }}</p>

            <div v-if="allArchivedPlayersData.length > 0" class="">
              <vue-good-table
                style-class="vgt-table bordered"
                :columns="columns"
                :rows="allArchivedPlayersData"
                :search-options="{
                  enabled: true,
                  placeholder: 'Search archived users',
                }"
              >
                <template v-slot:table-row="props">
                  <span v-if="props.column.field == 'dob'">
                    <span>{{
                      formatDates(props.formattedRow[props.column.field])
                    }}</span>
                  </span>
                  <span
                    class="action-buttons"
                    v-else-if="props.column.field == 'actions'"
                  >
                  </span>
                  <span v-else-if="props.column.field == 'userImage'">
                    <span
                      v-if="
                        props.row.userImage === null ||
                        props.row.userImage === ''
                      "
                    >
                      <img
                        v-if="
                          props.row.userImage === null ||
                          props.row.userImage === ''
                        "
                        :src="getImgUrl(props.row.userImage)"
                        alt=""
                        class="user-image-small"
                      />
                    </span>
                    <span v-else>
                      <TenantLogo
                        logo-class="login-logo"
                        class="user-image-small"
                      />
                    </span>
                  </span>
                </template>
              </vue-good-table>
            </div>
            <div v-else>
              <div class="empty-state">
                <img src="../img/emptyStateIllustration.svg" />
                <p>No Users.</p>
              </div>
            </div>
          </template>

          <template v-if="selectedNav === 'players'">
            <div class="settings-wrapper">
              <h2>Players</h2>
            </div>

            <p>Total number of players: {{ allPlayersData.length }}</p>

            <div v-if="allPlayersData.length > 0" class="">
              <vue-good-table
                style-class="vgt-table bordered"
                :columns="columns"
                :rows="allPlayersData"
                :search-options="{
                  enabled: true,
                  placeholder: 'Search users',
                }"
              >
                <template v-slot:table-row="props">
                  <span v-if="props.column.field == 'dob'">
                    <span>{{
                      formatDates(props.formattedRow[props.column.field])
                    }}</span>
                  </span>
                  <span v-else-if="props.column.field == 'trialistId'">
                    <p v-if="props.row.trialistId === null">Signed player</p>
                    <p v-else>Trialist</p>
                  </span>

                  <span
                    class="action-buttons"
                    v-else-if="props.column.field == 'actions'"
                  >
                    <b-button
                      @click="openEditUserModal(props.row)"
                      variant="outline-warning"
                      v-b-tooltip.hover
                      title="Edit"
                      ><i class="bi bi-pencil" aria-hidden="true"></i
                    ></b-button>

                    <b-button
                      v-if="userData.userType === 'Admin'"
                      @click="goToPlayer(props.row)"
                      variant="outline-success"
                      v-b-tooltip.hover
                      title="view player profile"
                      ><i class="bi bi-person" aria-hidden="true"></i
                    ></b-button>

                    <b-button
                      v-if="userData.userType === 'Admin'"
                      @click="releasePlayerModal(props.row)"
                      variant="outline-danger"
                      v-b-tooltip.hover
                      title="Release Player"
                      ><i class="bi bi-person" aria-hidden="true"></i
                    ></b-button>
                  </span>
                  <span v-else-if="props.column.field == 'userImage'">
                    <span
                      v-if="
                        props.row.userImage === null ||
                        props.row.userImage === ''
                      "
                    >
                      <TenantLogo
                        logo-class="login-logo"
                        class="user-image-small"
                      />
                    </span>
                    <span v-else>
                      <img
                        :src="getImgUrl(props.row.userImage)"
                        alt=""
                        class="user-image-small"
                      />
                    </span>
                  </span>
                </template>
              </vue-good-table>
            </div>
            <div v-else>
              <div class="empty-state">
                <img src="../img/emptyStateIllustration.svg" />
                <p>No Users.</p>
              </div>
            </div>
          </template>

          <template v-if="selectedNav === 'staff'">
            <div class="settings-wrapper">
              <h2>Staff</h2>
              <button
                v-if="userData.userType === 'Admin'"
                @click="openAddStaffModal()"
                class="green-button"
              >
                Add staff
              </button>
            </div>

            <p>Total number of staff: {{ allStaffData.length }}</p>

            <div v-if="allStaffData.length > 0" class="">
              <vue-good-table
                style-class="vgt-table bordered"
                :columns="columnsStaff"
                :rows="allStaffData"
                :search-options="{
                  enabled: true,
                  placeholder: 'Search staff',
                }"
              >
                <template v-slot:table-row="props">
                  <span v-if="props.column.field == 'dob'">
                    <span>{{
                      formatDates(props.formattedRow[props.column.field])
                    }}</span>
                  </span>
                  <span
                    class="action-buttons"
                    v-else-if="props.column.field == 'actions'"
                  >
                    <b-button
                      @click="openEditUserModal(props.row)"
                      variant="outline-warning"
                      v-b-tooltip.hover
                      title="Edit"
                      ><i class="bi bi-pencil" aria-hidden="true"></i
                    ></b-button>
                    <b-button
                      v-if="userData.userType === 'Admin'"
                      @click="archiveUser(props.row)"
                      variant="outline-danger"
                      v-b-tooltip.hover
                      title="Archive staff member"
                      ><i class="bi bi-trash" aria-hidden="true"></i
                    ></b-button>
                  </span>
                  <span v-else-if="props.column.field == 'userImage'">
                    <span
                      v-if="
                        props.row.userImage === null ||
                        props.row.userImage === ''
                      "
                    >
                      <TenantLogo
                        logo-class="login-logo"
                        class="user-image-small"
                      />
                    </span>
                    <span v-else>
                      <img
                        :src="getImgUrl(props.row.userImage)"
                        alt=""
                        class="user-image-small"
                      />
                    </span>
                  </span>
                </template>
              </vue-good-table>
            </div>
            <div v-else>
              <div class="empty-state">
                <img src="../img/emptyStateIllustration.svg" />
                <p>No Users.</p>
              </div>
            </div>
          </template>
          <template v-if="selectedNav === 'oppositionTeams'">
            <div class="settings-wrapper">
              <h2>Opposition teams</h2>
              <button
                @click="openAddOppositionTeamModal()"
                class="green-button"
              >
                Add Opposition Team
              </button>

              <div v-if="leagueTeams.length > 0" class="">
                <vue-good-table
                  style-class="vgt-table bordered"
                  :columns="columnsOppositionTeams"
                  :rows="leagueTeams"
                  :search-options="{
                    enabled: true,
                    placeholder: 'Search teams',
                  }"
                >
                  <template v-slot:table-row="props">
                    <span v-if="props.column.field == 'dob'">
                      <span>{{
                        formatDates(props.formattedRow[props.column.field])
                      }}</span>
                    </span>
                    <span
                      class="action-buttons"
                      v-else-if="props.column.field == 'actions'"
                    >
                      <b-button
                        @click="openAddOppositionTeamModal(props.row)"
                        variant="outline-warning"
                        v-b-tooltip.hover
                        title="Edit"
                        ><i class="bi bi-pencil" aria-hidden="true"></i
                      ></b-button>
                      <b-button
                        @click="deleteOppositionTeam(props.row)"
                        variant="outline-danger"
                        v-b-tooltip.hover
                        title="Delete"
                        ><i class="bi bi-trash" aria-hidden="true"></i
                      ></b-button>
                    </span>
                  </template>
                </vue-good-table>
              </div>
              <div v-else>
                <div class="empty-state">
                  <img src="../img/emptyStateIllustration.svg" />
                  <p>No Teams.</p>
                </div>
              </div>
            </div>
          </template>
          <template v-if="selectedNav === 'userSettings'">
            <div class="settings-wrapper">
              <div class="password-wrapper form">
                <h2>Change password</h2>
                <div class="form__item">
                  <label class="form__label" for="password"
                    >Current Password</label
                  >

                  <input
                    class="form__input"
                    type="password"
                    v-model="oldPassword"
                    id="password"
                  />
                  <!--<p class="invalid-feedback" v-if="validationValues.password.state === true">{{ validationValues.password.text }}</p>-->
                </div>
                <div class="form__item">
                  <label class="form__label" for="password">New Password</label>
                  <input
                    class="form__input"
                    type="password"
                    v-model="newPassword"
                    id="password"
                  />
                  <!--<p class="invalid-feedback" v-if="validationValues.password.state === true">{{ validationValues.password.text }}</p>-->
                </div>

                <button @click="changePassword()" class="green-button">
                  Change Password
                </button>
              </div>
            </div>
          </template>
          <template v-if="selectedNav === 'playerDataSync'">
            <div class="settings-wrapper">
              <h2>PlayerData Sync</h2>
              <p>Sync your data with PlayerData</p>
              <button @click="syncPlayerDataData">Sync from PlayerData</button>
            </div>
          </template>

          <template v-if="selectedNav === 'cometSync'">
            <div class="settings-wrapper">
              <h2>Comet Sync</h2>
              <p>Sync your data with Comet</p>
              <div class="player-list">
                <h2>Comet Players</h2>

                <!-- Filters -->
                <div class="filters">
                  <select v-model="filters.level">
                    <option value="">All Levels</option>
                    <option value="Amateur">Amateur</option>
                    <option value="Professional">Professional</option>
                  </select>

                  <input
                    v-model="filters.search"
                    placeholder="Search players..."
                  />

                  <button @click="loadPlayers">Search</button>
                  <button @click="syncData">Sync from Comet</button>
                </div>

                <!-- Player Table -->
                <table v-if="cometPlayers.length > 0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>DOB</th>
                      <th>Nationality</th>
                      <th>Level</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="player in cometPlayers" :key="player.id">
                      <td>{{ player.comet_id }}</td>
                      <td>{{ player.name }}</td>
                      <td>{{ formatDate(player.date_of_birth) }}</td>
                      <td>{{ player.nationality }}</td>
                      <td>{{ player.level }}</td>
                      <td>{{ player.status }}</td>
                    </tr>
                  </tbody>
                </table>

                <!-- Statistics -->
                <div v-if="cometPlayersStatistics" class="stats">
                  <h3>Statistics</h3>
                  <p>Total Players: {{ cometPlayersStatistics.total }}</p>
                  <p>Last Sync: {{ cometPlayersStatistics.last_sync }}</p>
                </div>
              </div>
            </div>
          </template>

          <template v-if="selectedNav === 'seasonPhases'">
            <div class="settings-wrapper">
              <h2>Season Phases</h2>
              <p>
                Set the club season calendar. All teams inherit these dates for
                filtering games, injuries, wellness, performance, and nutrition
                data — unless they have a per-team override.
              </p>
              <SeasonPhaseSettings @saved="onSeasonPhasesSaved" />
            </div>
          </template>

          <template v-if="selectedNav === 'clubPayments'">
            <div class="settings-wrapper">
              <ClubPaymentsSetup
                v-if="userData.userType === 'Admin'"
                :userData="userData"
              />
            </div>
          </template>

          <template v-if="selectedNav === 'trials'">
            <div class="settings-wrapper">
              <TrialsInbox
                v-if="userData.userType === 'Admin'"
                :club-id="userData.clubId"
              />
            </div>
          </template>

          <template v-if="selectedNav === 'notificationSettings'">
            <div class="settings-wrapper">
              <h2>Notification Settings</h2>
              <div class="notification-settings-content">
                <div class="notification-info-card">
                  <div class="notification-info-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#007AFF"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                  </div>
                  <div class="notification-info-text">
                    <h4>Push Notifications on iOS</h4>
                    <p>
                      To receive push notifications on your
                      {{ isIPhone() ? "iPhone" : "iPad" }}, you need to add this
                      app to your Home Screen and open it from there.
                    </p>
                  </div>
                </div>

                <div
                  v-if="isIOSPromptDismissed()"
                  class="notification-reset-section"
                >
                  <p class="notification-status">
                    <span class="status-badge status-dismissed"
                      >Install prompt dismissed</span
                    >
                  </p>
                  <p class="notification-help-text">
                    You previously dismissed the Home Screen installation
                    prompt. Click below to see the instructions again.
                  </p>
                  <button @click="reEnableIOSPrompt()" class="green-button">
                    Show Install Instructions
                  </button>
                </div>

                <div v-else class="notification-status-section">
                  <p class="notification-status">
                    <span class="status-badge status-pending"
                      >Waiting for Home Screen install</span
                    >
                  </p>
                  <p class="notification-help-text">
                    The installation prompt will appear the next time you log
                    in.
                  </p>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <TeamSeasonDatesEditor
      :team="selectedTeamForSeasonEdit"
      :visible="showSeasonDatesEditor"
      @saved="onSeasonDatesSaved"
      @closed="closeSeasonDatesEditor"
    />

    <b-modal
      v-if="showEditTeam"
      v-model="showEditTeam"
      size="lg"
      title="Edit Team"
      :hide-footer="true"
    >
      <div class="form">
        <div class="form__item">
          <label class="form__label" for="teamName">Team name</label>
          <input
            class="form__input"
            type="text"
            v-model="selectedTeam.teamName"
            id="teamName"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="leagueName">League name</label>
          <input
            class="form__input"
            type="text"
            v-model="selectedTeam.leagueName"
            id="leagueName"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="league_link">League URL</label>
          <input
            class="form__input"
            type="text"
            v-model="selectedTeam.league_link"
            id="league_link"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="age_group">Age group</label>
          <input
            class="form__input"
            type="text"
            v-model="selectedTeam.age_group"
            id="age_group"
            maxlength="4"
            pattern="\d{4}"
          />
        </div>

        <div class="form__item uk-form dob">
          <label class="form__label" for="season_start_date"
            >Season start date</label
          >
          <VueDatePicker
            :locale="'en-GB'"
            format="dd/MM/yyyy"
            text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="selectedTeam.season_start_date"
            name="season_start_date"
            id="season_start_date"
            :enable-time-picker="false"
          />
        </div>

        <div class="form__item uk-form dob">
          <label class="form__label" for="season_end_date"
            >Season end date</label
          >
          <VueDatePicker
            :locale="'en-GB'"
            format="dd/MM/yyyy"
            text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="selectedTeam.season_end_date"
            name="season_end_date"
            id="season_end_date"
            :enable-time-picker="false"
          />
        </div>

        <div class="form__item">
          <label
            v-b-tooltip.hover
            title="Each team must have a coach assigned to them"
            class="form__label"
            for="name"
            >Add coach
            <i
              class="bi bi-exclamation-circle"
              style="width: 24px; height: 24px"
            ></i
          ></label>
          <div v-if="changeHeadCoach === false">
            <span class="current-coach-change-link"
              >Head coach: {{ getHeadCoachName() }}</span
            >
            <a @click="changeHeadCoach = true" href="javascript:;"
              >Change head coach</a
            >
          </div>
          <div v-else class="wrap">
            <input
              class="form__input"
              type="text"
              v-model="selectedCoach.name"
              id="trainingPlanId"
            />
            <button @click="findCoachByName">Find coach by name</button>
          </div>
          <div></div>
          <div v-if="changeHeadCoach === true" class="training-plan-display">
            Head Coach: {{ selectedTeam.name }}
            <b-form-group
              v-if="selectedCoachesData.length > 0"
              class="find-coach-radio-wrapper"
            >
              <b-form-radio
                v-for="coach in selectedCoachesData"
                :key="coach.id"
                name="coach-radios"
                :value="coach.id"
                @change="
                  updateCurrentSelectedTeamCoachName(
                    coach.name,
                    coach.id,
                    selectedTeam.coach_id,
                    'Head',
                  )
                "
                >{{ coach.name }}</b-form-radio
              >
            </b-form-group>
            <p v-else>No coaches found</p>
          </div>
          <div v-else></div>
          <div></div>
          <p class="coach-header">Teams additional coaches list</p>

          <div></div>
          <div class="access-pool">
            <div
              v-for="coachs in selectedCoachesForSelectedTeam"
              :key="coachs.id"
            >
              <span
                class="has-access"
                v-if="coachs.coach_id !== selectedTeam.coach_id"
                >Coach: {{ coachs.name }}
                <a @click="removeCoachFromTeam(coachs.coach_id)">x</a></span
              >
            </div>
          </div>

          <div></div>
          <div>
            <a @click="addAnotherCoach = true" href="javascript:;"
              >Add another coach</a
            >

            <div v-if="addAnotherCoach === true" class="add-anotherCoach">
              <input
                class="form__input"
                type="text"
                v-model="selectedCoach.name"
                id="trainingPlanId"
              />
              <button @click="findCoachByName">Find coach by name</button>

              <div class="training-plan-display">
                <b-form-group
                  v-if="selectedCoachesData.length > 0"
                  class="find-coach-radio-wrapper"
                >
                  <b-form-radio
                    v-for="coach in selectedCoachesData"
                    :key="coach.id"
                    name="coach-radios"
                    :value="coach.id"
                    @change="
                      addAnotherCoachCall(coach.id, selectedTeam.id, 'Coach')
                    "
                    >{{ coach.name }}</b-form-radio
                  >
                </b-form-group>
                <p v-else>No coaches found</p>
              </div>
            </div>
          </div>
        </div>

        <p class="small-text">
          Please note that the start and end dates of the season will define
          when the app begins recording all data. If there are any activities
          occurring outside the designated season that you wish to view within
          the app, we recommend commencing your season ahead of time.
        </p>

        <button @click="upDateTeam()" class="green-button">Edit Team</button>
      </div>
    </b-modal>

    <b-modal
      v-if="showAddNewTeam"
      v-model="showAddNewTeam"
      size="lg"
      title="Add a new team"
      :hide-footer="true"
    >
      <div class="form">
        <div class="form__item">
          <label class="form__label" for="teamName">Team name</label>
          <input
            class="form__input"
            type="text"
            v-model="newTeamData.teamName"
            id="teamName"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="age_group">Age group</label>
          <input
            class="form__input"
            type="text"
            v-model="newTeamData.age_group"
            id="age_group"
            maxlength="4"
            pattern="\d{4}"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="leagueName">league name</label>
          <input
            class="form__input"
            type="text"
            v-model="newTeamData.leagueName"
            id="leagueName"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="league_link"
            >Link to the league table</label
          >
          <input
            class="form__input"
            type="text"
            v-model="newTeamData.league_link"
            id="league_link"
          />
        </div>

        <div class="form__item uk-form dob">
          <label class="form__label" for="season_start_date"
            >Season start date</label
          >
          <VueDatePicker
            :locale="'en-GB'"
            format="dd/MM/yyyy"
            text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="newTeamData.season_start_date"
            name="season_start_date"
            id="season_start_date"
            :enable-time-picker="false"
          />
        </div>

        <div class="form__item uk-form dob">
          <label class="form__label" for="season_end_date"
            >Season end date</label
          >
          <VueDatePicker
            :locale="'en-GB'"
            format="dd/MM/yyyy"
            text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="newTeamData.season_end_date"
            name="season_end_date"
            id="season_end_date"
            :enable-time-picker="false"
          />
        </div>

        <div class="form__item">
          <label
            v-b-tooltip.hover
            title="Each team must have a coach assigned to them"
            class="form__label"
            for="name"
            >Add coach
            <i
              class="bi bi-exclamation-circle"
              style="width: 24px; height: 24px"
            ></i
          ></label>
          <div class="wrap">
            <input
              class="form__input"
              type="text"
              v-model="selectedCoach.name"
              id="trainingPlanId"
            />
            <button @click="findCoachByName">Find coach by name</button>
          </div>
          <div></div>
          <div class="training-plan-display">
            <b-form-group
              v-if="selectedCoachesData.length > 0"
              class="find-coach-radio-wrapper"
            >
              <b-form-radio
                v-for="coach in selectedCoachesData"
                :key="coach.id"
                v-model="selectedCoach.id"
                name="coach-radios"
                :value="coach.id"
                @change="
                  updateCurrentSelectedTeamCoachName(
                    coach.name,
                    coach.id,
                    selectedTeam.coach_id,
                    'Head',
                  )
                "
                >{{ coach.name }}</b-form-radio
              >
            </b-form-group>
            <p v-else>No coaches found</p>
          </div>
          <div></div>
          <p v-if="missingCoachId === true" class="red-warning">
            <i class="bi bi-exclamation-circle" aria-hidden="true"></i> You need
            to add a coach.
          </p>
        </div>

        <p class="small-text">
          Please note that the start and end dates of the season will define
          when the app begins recording all data. If there are any activities
          occurring outside the designated season that you wish to view within
          the app, we recommend commencing your season ahead of time.
        </p>

        <button @click="addNewTeam()" class="green-button">Add Team</button>
      </div>
    </b-modal>

    <b-modal
      v-if="showAddStaff"
      v-model="showAddStaff"
      size="lg"
      title="Add staff member"
      :hide-footer="true"
    >
      <div class="form">
        <template v-if="!addStaffResult">
          <div class="form__item">
            <label class="form__label" for="staffName">Full name</label>
            <input
              class="form__input"
              type="text"
              v-model="newStaff.name"
              id="staffName"
            />
          </div>
          <div class="form__item">
            <label class="form__label" for="staffEmail"
              >Email (this is their login)</label
            >
            <input
              class="form__input"
              type="email"
              v-model="newStaff.email"
              id="staffEmail"
            />
          </div>
          <div class="form__item">
            <label class="form__label" for="staffRole">Role</label>
            <select
              class="form__input"
              id="staffRole"
              v-model="newStaff.userType"
            >
              <option value="Coach">Coach</option>
              <option value="Admin">Admin</option>
              <option value="Scout">Scout</option>
              <option value="SportsScience">Sports Science (testing only)</option>
            </select>
            <p class="form__hint" v-if="newStaff.userType === 'SportsScience'">
              Restricted role for volunteer sports scientists: can record physical
              testing (height, weight, jumps, sprints, growth &amp; maturation) for
              any squad and use club chat groups they're added to. No access to
              profiles, medical, wellness, injuries, contacts or documents. Switch
              off later by archiving the user.
            </p>
          </div>
          <div class="form__item">
            <label class="form__label" for="staffPosition"
              >Title (optional)</label
            >
            <select
              class="form__input"
              id="staffPosition"
              v-model="newStaff.position"
            >
              <option value="">—</option>
              <option value="HeadCoach">Head Coach</option>
              <option value="Coach">Coach</option>
              <option value="Goalkeeper coach">Goalkeeper coach</option>
              <option value="Physio">Physio</option>
              <option value="Scout">Scout</option>
            </select>
          </div>
          <div class="form__item">
            <label class="form__label" for="staffPhone">Phone (optional)</label>
            <input
              class="form__input"
              type="text"
              v-model="newStaff.phone"
              id="staffPhone"
            />
          </div>
          <div class="form__item">
            <label class="form__label" for="staffTeam"
              >Assign to team (optional)</label
            >
            <select
              class="form__input"
              id="staffTeam"
              v-model="newStaff.teamId"
            >
              <option :value="0">— No team —</option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ team.teamName }}
              </option>
            </select>
          </div>

          <p v-if="addStaffError" class="red-warning">
            <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
            {{ addStaffError }}
          </p>

          <button
            @click="submitAddStaff()"
            :disabled="addingStaff"
            class="green-button"
          >
            {{ addingStaff ? "Adding…" : "Add staff member" }}
          </button>
        </template>

        <div v-else class="staff-add-result">
          <p>
            <strong>{{ addStaffResult.name }}</strong> has been added.
          </p>
          <p>
            Login email: <strong>{{ addStaffResult.email }}</strong
            ><br />
            Temporary password:
            <strong>{{ addStaffResult.tempPassword }}</strong>
          </p>
          <p v-if="addStaffResult.emailSent">
            <i class="bi bi-check-circle" aria-hidden="true"></i>
            An invite email with these details has been sent to them.
          </p>
          <p v-else class="red-warning">
            <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
            The invite email couldn't be sent — please pass these details to
            them directly.
          </p>
          <p class="small-text">
            They sign in with the email + temporary password, then change it
            from Settings.
          </p>
          <button @click="closeAddStaffModal()" class="green-button">
            Done
          </button>
        </div>
      </div>
    </b-modal>
    <b-modal
      v-if="showAddOppositionTeam"
      v-model="showAddOppositionTeam"
      size="lg"
      :title="
        editMode === false ? 'Add Opposition Team' : 'Edit Opposition Team'
      "
      :hide-footer="true"
    >
      <div class="form">
        <div class="form__item">
          <label class="form__label" for="teamName">Team name</label>
          <input
            class="form__input"
            type="text"
            v-model="selectedOppositionTeam.teamName"
            id="name"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="teamName">Pitch name</label>
          <input
            class="form__input"
            type="text"
            v-model="selectedOppositionTeam.name"
            id="name"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="address">Pitch address</label>

          <b-form-textarea
            v-model="selectedOppositionTeam.address"
            id="address"
            placeholder="Add address"
            rows="8"
          ></b-form-textarea>
        </div>

        <div class="form__item">
          <label class="form__label" for="postCode">PostCode</label>
          <input
            id="postCode"
            type="text"
            v-model="selectedOppositionTeam.postCode"
            name="postCode"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="pitchType">Pitch size</label>

          <b-form-select v-model="selectedOppositionTeam.pitch_size">
            <option disabled value="">Please select a pitch size</option>
            <option value="11-a-side">11-a-side</option>
            <option value="9-a-side">9-a-side</option>
            <option value="7-a-side">7-a-side</option>
            <option value="5-a-side">5-a-side</option>
            <option value="Other">Other</option>
          </b-form-select>
        </div>

        <div class="form__item">
          <label class="form__label" for="pitchType">Pitch type</label>

          <b-form-select v-model="selectedOppositionTeam.pitchType">
            <option disabled value="">Please select a pitch type</option>
            <option value="4G">4G</option>
            <option value="3G">3G</option>
            <option value="Grass">Grass</option>
            <option value="Other">Other</option>
          </b-form-select>
        </div>
      </div>

      <button
        v-if="editMode === false"
        class="green-button"
        @click="addOppositionTeam()"
      >
        Add team
      </button>

      <button v-else class="green-button" @click="editOppositionTeam()">
        Update team
      </button>
    </b-modal>

    <b-modal
      v-if="showAreYouSureModal"
      v-model="showAreYouSureModal"
      size="lg"
      title="Archive Player"
      :hide-footer="true"
      @hidden="editUserModalClose()"
    >
      <h3>Are you sure?</h3>
      <p>
        If you proceed, this player will be archived. This action will remove
        them from all team activities and app functions, and they will lose
        access to the app entirely..
      </p>

      <b-button
        @click="archiveUser(playerToBeReleased)"
        variant="outline-danger"
        ><i class="bi bi-person" aria-hidden="true"></i> Release
        Player</b-button
      >
    </b-modal>

    <b-modal
      v-if="showEditUser"
      v-model="showEditUser"
      size="lg"
      title="Edit Player"
      :hide-footer="true"
      @hidden="editUserModalClose()"
    >
      <div class="form">
        <div class="form__item">
          <label class="form__label" for="name">Name</label>
          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.name"
            id="name"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="email">Email</label>
          <input
            :class="
              validationValues.email.state === true
                ? 'danger-outline form__input'
                : 'form__input'
            "
            type="email"
            @change="checkChangeVlaidation(selectedPlayer.email, 'email')"
            v-model="selectedPlayer.email"
            id="email"
          />
          <p
            class="invalid-feedback"
            v-if="validationValues.email.state === true"
          >
            {{ validationValues.email.text }}
          </p>
        </div>

        <div class="form__item uk-form dob">
          <label class="form__label" for="dob">DOB</label>
          <VueDatePicker
            :locale="'en-GB'"
            format="dd/MM/yyyy"
            text-input-options="{ format: 'dd/MM/yyyy' }"
            :class="
              selectedPlayer.dob === null ||
              selectedPlayer.dob === 'Invalid date'
                ? 'danger-outline form__input'
                : 'form__input'
            "
            v-model="selectedPlayer.dob"
            name="dob"
            id="dob"
            :enable-time-picker="false"
            @update:model-value="selectedPlayer.dob = $event"
          />
          <p
            class="invalid-feedback"
            v-if="
              selectedPlayer.dob === null ||
              selectedPlayer.dob === 'Invalid date'
            "
          >
            {{ validationValues.dob.text }}
          </p>
        </div>

        <ImageUploader
          @populateImageObjectFromUpload="imageUploadFileDataChnged($event)"
          @populateImageFileDataFromUpload="
            imageUploadObjectDataChanged($event)
          "
          @clearImageDataFromUpload="clearImageDataFromUpload()"
          :imageURL="selectedPlayer.userImage"
        />

        <div class="form__item" v-if="selectedPlayer.userType !== 'User'">
          <label class="form__label" for="position">Job role</label>

          <select id="position" v-model="selectedPlayer.position">
            <option disabled value="">Please select a role</option>
            <option value="HeadCoach">Head Coach</option>
            <option value="Coach">Coach</option>
            <option value="Goalkeeper coach">Goalkeeper Coach</option>
            <option value="Academy Director">Academy Director</option>
            <option value="Head Sports Science">Head Sports Science</option>
            <option value="Sports Science">Sports Science</option>
            <option value="Head Of Recruitment">Head of Recruitment</option>
            <option value="Scout">Scout</option>
            <option value="Head Physio">Head Physio</option>
            <option value="Physio">Physio</option>
            <option value="Head Of Professional Phase">
              Head of Professional phase
            </option>
            <option value="Head Of Childrens Acadmey">
              Head of Childrens Acadmey
            </option>
            <option value="Head Of Youth Academy">Head of Youth Academy</option>
            <option value="Welfare Officer">Welfare Officer</option>
            <option value="Head Of Analyst">Head Of Analyst</option>
            <option value="Analyst">Analyst</option>
            <option value="Academy Admin">Academy Admin</option>
          </select>
        </div>

        <div class="form__item" v-if="selectedPlayer.userType === 'User'">
          <label class="form__label" for="position">Position</label>

          <select id="position" v-model="selectedPlayer.position">
            <option disabled value="">Please select a position</option>
            <option value="Goalkeeper">Goalkeeper</option>
            <option value="Right Full-back (Wingback)">
              Right Full-back (Wingback)
            </option>
            <option value="Left Full-back (Wingback)">
              Left Full-back (Wingback)
            </option>
            <option value="Center-back left">Center-back left</option>
            <option value="Center-back right">Center-back right</option>
            <option value="Defensive Midfielder">Defensive Midfielder</option>
            <option value="Centre midfield">Centre midfield</option>
            <option value="Centre Defensive Midfield">
              Centre Defensive Midfield
            </option>
            <option value="Right Midfielder (Winger)">
              Right Midfielder (Winger)
            </option>
            <option value="Left Midfielder (Winger)">
              Left Midfielder (Winger)
            </option>
            <option value="Attacking Midfielder">Attacking Midfielder</option>
            <option value="Center Forward (Striker) left">
              Center Forward (Striker) left
            </option>
            <option value="Center Forward (Striker) right">
              Center Forward (Striker) right
            </option>
            <option value="I played more than one position">
              I played more than one position
            </option>
          </select>
        </div>

        <div class="form__item">
          <label class="form__label" for="phone">Phone</label>
          <input
            class="form__input"
            type="number"
            v-model="selectedPlayer.phone"
            id="phone"
          />
        </div>

        <div class="form__item" v-if="selectedPlayer.userType === 'User'">
          <label class="form__label" for="parent_name">Parent name</label>
          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.parent_name"
            id="parent_name"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="emergency_contact_one"
            >Emergency Contact one</label
          >
          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.emergency_contact_one"
            id="emergency_contact_one"
          />
        </div>

        <div class="form__item">
          <label class="form__label" for="emergency_contact_two"
            >Emergency Contact two</label
          >
          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.emergency_contact_two"
            id="emergency_contact_two"
          />
        </div>

        <div
          class="form__item uk-form joined_date"
          v-if="selectedPlayer.userType === 'User'"
        >
          <label class="form__label" for="joined_date">Joined date</label>
          <VueDatePicker
            :locale="'en-GB'"
            format="dd/MM/yyyy"
            text-input-options="{ format: 'dd/MM/yyyy' }"
            :class="
              selectedPlayer.dob === null ||
              selectedPlayer.joined_date === 'Invalid date'
                ? 'danger-outline form__input'
                : 'form__input'
            "
            v-model="selectedPlayer.joined_date"
            name="joined_date"
            id="joined_date"
            :enable-time-picker="false"
            @update:model-value="selectedPlayer.joined_date = $event"
          />
          <p
            class="invalid-feedback"
            v-if="
              selectedPlayer.joined_date === null ||
              selectedPlayer.joined_date === 'Invalid date'
            "
          >
            {{ validationValues.joinedDate.text }}
          </p>
        </div>

        <div class="form__item">
          <label class="form__label" for="emergency_contact_one">Address</label>

          <b-form-textarea
            v-model="selectedPlayer.address"
            rows="8"
            id="emergency_contact_one"
          ></b-form-textarea>
        </div>

        <div class="form__item uk-form post_code">
          <label class="form__label" for="post_code">Post code</label>

          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.post_code"
            id="post_code"
          />
        </div>

        <div
          class="form__item uk-form place_of_birth"
          v-if="selectedPlayer.userType === 'User'"
        >
          <label class="form__label" for="place_of_birth">Place of birth</label>

          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.place_of_birth"
            id="place_of_birth"
          />
        </div>

        <div
          class="form__item uk-form place_of_birth"
          v-if="selectedPlayer.userType === 'User'"
        >
          <label class="form__label" for="country_of_birth"
            >Country of birth</label
          >

          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.country_of_birth"
            id="place_of_birth"
          />
        </div>

        <div
          class="form__item uk-form nationality"
          v-if="selectedPlayer.userType === 'User'"
        >
          <label class="form__label" for="nationality">Nationality</label>

          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.nationality"
            id="nationality"
          />
        </div>

        <div
          class="form__item uk-form nationality"
          v-if="selectedPlayer.userType === 'User'"
        >
          <label class="form__label" for="second_nationality"
            >Second nationality (dual citizenship)</label
          >

          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.second_nationality"
            id="second_nationality"
          />
        </div>

        <div
          class="form__item uk-form previous_club"
          v-if="selectedPlayer.userType === 'User'"
        >
          <label class="form__label" for="nationality">Previous club</label>

          <input
            class="form__input"
            type="text"
            v-model="selectedPlayer.previous_club"
            id="previous_club"
          />
        </div>

        <div class="form__item" v-if="selectedPlayer.userType === 'User'">
          <label class="form__label" for="match_day_kit_number"
            >kit number</label
          >
          <input
            class="form__input"
            type="number"
            v-model="selectedPlayer.match_day_kit_number"
            id="match_day_kit_number"
          />
        </div>

        <div class="form__item" v-if="selectedPlayer.userType === 'User' && selectedPlayer.id">
          <label class="form__label">Medical information</label>
          <!-- Structured record, saved independently of the profile form via
               back-end-endpoint-medical.php. @saved refreshes the legacy
               summary so "Save" below doesn't overwrite it with a stale value. -->
          <MedicalInfoCard :player-id="selectedPlayer.id" :club-id="selectedPlayer.clubId || userData.clubId"
            :can-edit="true" :viewer-id="userData.id" @saved="onMedicalSaved" />
        </div>

        <div
          v-if="
            selectedPlayer.userType !== 'Admin' &&
            selectedPlayer.userType !== 'Coach'
          "
          class="form__item"
        >
          <label class="form__label" for="emergency_contact_one"
            >Primary team</label
          >
          <div v-if="getTeamNameFromId(selectedPlayer.teamId) !== ''">
            <p>
              {{ getTeamNameFromId(selectedPlayer.teamId) }}
              <a @click="showTeamsSelection = true" href="javascript:;"
                >- Change primary team</a
              >
            </p>

            <div class="form__item" v-if="showTeamsSelection === true">
              <label class="form__label" for="coachSelect">Select team</label>
              <select
                v-model="selectedPlayerTeamValue"
                id="coachSelect"
                @change="changePlayersTeam($event)"
              >
                <option disabled value="">Please select a team</option>
                <option v-for="team in teams" :value="team.id" :key="team.id">
                  {{ team.teamName }}
                </option>
              </select>
            </div>

            <div class="more-team-information">
              <h3>
                {{ selectedPlayer.name }} is a memebr of
                {{ playersOtherTeamsData.length }} other team<span
                  v-if="playersOtherTeamsData.length > 1"
                  >'s</span
                >
              </h3>

              <div class="extra-teams-list">
                <p v-for="team in playersOtherTeamsData" :key="team.id">
                  {{ team.teamName }} -
                  <a
                    class="red-warning"
                    @click="removePlayerFromTeam(selectedPlayer.id, team.id)"
                    href="javascript:;"
                    >remove</a
                  >
                </p>
              </div>

              <a
                class="add-other-team-button"
                v-if="selectedPlayer.teamId !== 0"
                @click="showTeamsSelectionAddTeam = true"
                href="javascript:;"
                >Add another team</a
              >

              <div class="form__item" v-if="showTeamsSelectionAddTeam === true">
                <label class="form__label" for="coachSelect">Select team</label>
                <select
                  v-model="selectedPlayerTeamValue"
                  id="coachSelect"
                  @change="addPlayerToNewTeam($event)"
                >
                  <option disabled value="">Please select a team</option>
                  <option v-for="team in teams" :value="team.id" :key="team.id">
                    {{ team.teamName }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <p v-else>
            No current team
            <a @click="showTeamsSelection = true" href="javascript:;"
              >- Connect this player to a team</a
            >
          </p>
        </div>

        <button
          v-if="loadingStuff === false"
          @click="upDatePlayerProfile()"
          class="green-button"
        >
          Edit profile
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import { CHECK_PROFILE_HAS_DATA, USER_POPULATE } from "@/store/actions/user.js";
import { Users } from "@/services/user-service.js";
import { Teams } from "@/services/teams-service.js";
import { Games } from "@/services/games-service.js";
import AppTemplate from "../layouts/App";
import moment from "moment";
import SpinnerLoader from "../../components/LoadingSpinning";
import ImageUploader from "../../components/ImageUploader";
import VueGoodTable from "vue-good-table-next";
import "vue-good-table-next/dist/vue-good-table-next.css";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import goToPlayerMixin from "@/mixin/goToPlayer.js";
import iosDetectionMixin from "@/mixin/iosDetection.js";
import { getAuth, updatePassword } from "firebase/auth";
import TenantLogo from "@/components/TenantLogo/index.vue";
import SeasonPhaseSettings from "@/components/SeasonPhaseSettings/index.vue";
import TeamSeasonDatesEditor from "@/components/TeamSeasonDatesEditor/index.vue";
import ClubPaymentsSetup from "@/components/ClubPaymentsSetup/index.vue";
import TrialsInbox from "@/components/TrialsInbox/index.vue";
import SectionNav from "@/components/SectionNav/index.vue";
import MedicalInfoCard from "@/components/MedicalInfo/MedicalInfoCard.vue";
import { isFeatureEnabled, getSeasonWindow } from "@/config/tenant";
import axios from "axios";

Vue.use(VueGoodTable);

export default {
  name: "Settings",
  mixins: [goToPlayerMixin, iosDetectionMixin],
  components: {
    AppTemplate,
    SpinnerLoader,
    VueDatePicker,
    ImageUploader,
    TenantLogo,
    SeasonPhaseSettings,
    TeamSeasonDatesEditor,
    ClubPaymentsSetup,
    TrialsInbox,
    SectionNav,
    MedicalInfoCard,
  },
  data() {
    return {
      cometPlayers: [],
      cometPlayersStatistics: null,
      filters: {
        level: "",
        search: "",
      },
      britishLocaleData: {
        direction: "ltr",
        format: "dd/MM/yyyy",
        separator: " - ",
        applyLabel: "Apply",
        cancelLabel: "Cancel",
        weekLabel: "W",
        customRangeLabel: "Custom Range",
        daysOfWeek: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        monthNames: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        firstDay: 1, // Monday first (British standard)
      },
      // Modal visibility properties
      showEditTeam: false,
      showAddNewTeam: false,
      showAddOppositionTeam: false,
      showAreYouSureModal: false,
      showEditUser: false,
      showSeasonDatesEditor: false,
      selectedTeamForSeasonEdit: null,
      needsValidation: [],
      validationMessage: null,
      validationValues: {
        name: {
          state: false,
          text: "Please add a name please!",
        },
        email: {
          state: false,
          text: "Please add an email address",
        },
        dob: {
          state: false,
          text: "Please add DOB please!",
        },
        joinedDate: {
          state: false,
          text: "Please add joined date please!",
        },
      },
      missingCoachId: false,
      addAnotherCoach: false,
      changeHeadCoach: false,
      changeCoach: false,
      selectedCoachesData: [],
      selectedCoach: {},
      selectedPlayerTeamValue: null,
      oldPassword: null,
      newPassword: null,
      loadingStuff: false,
      allPlayersData: [],
      allStaffData: [],
      adminUserData: [],
      allArchivedPlayersData: [],
      selectedNav: "teams",
      navCollapsed: false,
      showTeamsSelection: false,
      showTeamsSelectionAddTeam: false,
      teams: [],
      // Add-staff modal state
      showAddStaff: false,
      addingStaff: false,
      addStaffError: null,
      addStaffResult: null,
      newStaff: {
        name: "",
        email: "",
        userType: "Coach",
        position: "",
        phone: "",
        teamId: 0,
      },
      newTeamData: {
        teamName: "",
        leagueName: "",
        clubId: "",
        league_link: "",
        season_start_date: null,
        season_end_date: null,
        age_group: "0000",
      },
      userData: {
        name: "",
      },
      columnsOppositionTeams: [
        {
          label: "Team name",
          field: "teamName",
        },
        {
          label: "Pitch type",
          field: "pitchType",
        },
        {
          label: "Pitch size",
          field: "pitch_size",
        },
        {
          label: "Actions",
          width: "6rem",
          field: "actions",
        },
      ],
      columnsTeams: [
        {
          label: "",
          field: "teamIcon",
        },
        {
          label: "Team Name",
          field: "teamName",
        },
        {
          label: "League Name",
          field: "leagueName",
        },
        {
          label: "Season Start Date",
          field: "season_start_date",
        },
        {
          label: "Season End Date",
          field: "season_end_date",
        },
        {
          label: "Age Group",
          field: "age_group",
        },
        {
          label: "coach_id",
          field: "coach_id",
        },
        {
          label: "Coach name",
          field: "name",
        },
        {
          label: "Actions",
          width: "6rem",
          field: "actions",
        },
      ],
      columns: [
        {
          label: "image",
          field: "userImage",
        },
        {
          label: "Name",
          field: "name",
        },
        {
          label: "Position",
          field: "position",
        },
        {
          label: "Age group",
          field: "age_group",
        },
        {
          label: "Dob",
          field: "dob",
        },
        {
          label: "Team",
          field: "teamName",
        },
        {
          label: "Type",
          field: "trialistId",
        },
        {
          label: "Id",
          field: "id",
        },
        {
          label: "Actions",
          width: "6rem",
          field: "actions",
        },
      ],
      columnsStaff: [
        {
          label: "image",
          field: "userImage",
        },
        {
          label: "Name",
          field: "name",
        },
        {
          label: "user type",
          field: "userType",
        },

        {
          label: "Dob",
          field: "dob",
        },
        {
          label: "Actions",
          width: "6rem",
          field: "actions",
        },
      ],
      selectedOppositionTeam: {},
      selectedPlayer: {},
      selectedTeam: {},
      selectedCoachesForSelectedTeam: [],
      imageFile: null,
      imageToBeRemoved: null,
      imageURL: "",
      image: {
        imageName: "",
        path: "",
        file: null,
      },
      playersOtherTeamsData: [],
      leagueTeams: [],
      editMode: false,
    };
  },
  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(["getProfile"]),
    // Backend base: localhost in dev (port 8080), same-origin relative path in
    // production. Hardcoding localhost here broke the sync in production (CORS).
    apiBase() {
      return window.location.href.indexOf("8080") > -1
        ? "http://www.localhost:8888"
        : "/personal-training-BE";
    },
    cometPlayerApiUrl() {
      return `${this.apiBase}/back-end-endpoint-comet-data.php`;
    },
    playerDataApiUrl() {
      return `${this.apiBase}/back-end-endpoint-playerdata.php`;
    },
    // Left drawer nav items (SectionNav). Mirrors the old side-nav <ul>,
    // including its role/feature/platform gating.
    settingsNavItems() {
      const isStaff =
        this.userData.userType === "Admin" ||
        this.userData.userType === "Coach";
      const isAdmin = this.userData.userType === "Admin";
      const items = [];
      if (isStaff) items.push({ key: "teams", label: "Teams", icon: "groups" });
      items.push({
        key: "players",
        label: isStaff ? "Players" : "Profile",
        icon: "person",
      });
      if (isStaff)
        items.push({
          key: "oppositionTeams",
          label: "Opposition teams",
          icon: "shield",
        });
      items.push({
        key: "userSettings",
        label: "Change Password",
        icon: "lock",
      });
      if (isStaff)
        items.push({
          key: "archivedPlayers",
          label: "Archived Players",
          icon: "archive",
        });
      if (process.env.NODE_ENV === "development")
        items.push({ key: "cometSync", label: "Comet sync", icon: "sync" });
      if (this.userData.clubId === "ptfc-2085XH")
        items.push({
          key: "playerDataSync",
          label: "PlayerData sync",
          icon: "cloud_sync",
        });
      if (isStaff) items.push({ key: "staff", label: "Staff", icon: "badge" });
      if (isAdmin)
        items.push({
          key: "seasonPhases",
          label: "Season Phases",
          icon: "calendar_month",
        });
      if (isAdmin && this.hasFeature("paymentProcessing"))
        items.push({
          key: "clubPayments",
          label: "Club Payments",
          icon: "payments",
        });
      if (isAdmin && this.hasFeature("trialRegistration"))
        items.push({
          key: "trials",
          label: "Trials",
          icon: "how_to_reg",
        });
      if (this.isIOSSafari() && !this.isStandalone())
        items.push({
          key: "notificationSettings",
          label: "Notifications",
          icon: "notifications",
        });
      return items;
    },
  },
  async created() {
    this.loadingStuff = true;
    const response = await this.$store.dispatch(CHECK_PROFILE_HAS_DATA);

    if (response === true) {
      this.setUser();
      this.teamsAndUserCall();

      this.loadingStuff = false;
    } else {
      const clientId = JSON.parse(
        localStorage.getItem("the_w_selectedClientId"),
      );

      this.getUserByIdCall(clientId);
      this.loadingStuff = false;
    }
    this.getAllOppositionTeams();

    // Returning from Stripe Connect onboarding (?stripe=return|refresh):
    // land the admin straight back on the Club Payments section
    if (this.$route.query.stripe) {
      this.selectedNav = "clubPayments";
    }
  },
  methods: {
    onMedicalSaved(record) {
      if (!record) return;
      this.selectedPlayer = {
        ...this.selectedPlayer,
        medical: record.summary,
        has_medical_conditions: record.hasConditions,
      };
    },
    hasFeature(feature) {
      return isFeatureEnabled(feature);
    },
    reEnableIOSPrompt() {
      this.resetIOSPromptDismissal();
      this.$toast.success(
        "Install prompt re-enabled. You'll see the instructions on your next login.",
        {
          toaster: "b-toaster-bottom-center",
          autoHideDelay: 4000,
        },
      );
      // Force re-render to update the UI
      this.$forceUpdate();
    },
    onSeasonPhasesSaved() {
      // Optional: Show a notification or refresh data
      console.log("Season phases configuration saved successfully");
    },
    async loadPlayers() {
      try {
        const params = new URLSearchParams({ action: "getPlayers" });

        if (this.filters.level) params.append("level", this.filters.level);
        if (this.filters.search) params.append("search", this.filters.search);

        const response = await axios.get(`${this.cometPlayerApiUrl}?${params}`);

        if (response.data.success) {
          this.players = response.data.players;
        }
      } catch (error) {
        console.error("Error loading players:", error);
      }
    },
    async loadStatistics() {
      try {
        const response = await axios.get(
          `${this.cometPlayerApiUrl}?action=getStatistics`,
        );

        if (response.data.success) {
          this.statistics = response.data.statistics;
        }
      } catch (error) {
        console.error("Error loading statistics:", error);
      }
    },
    async syncPlayerDataData() {
      try {
        const response = await axios.get(
          `${this.playerDataApiUrl}?action=syncAll&limit=50`,
        );

        console.log("Sync Player Data Response:", response.data);

        if (response.status === 200) {
          console.log("Player data sync completed successfully!");
          this.syncDetailedMetrics();
        }
      } catch (error) {
        console.error("Error syncing player data:", error);
      }
    },
    async syncDetailedMetrics() {
      try {
        const response = await axios.get(
          `${this.playerDataApiUrl}?action=detailedMetrics`,
        );

        if (response.data.success) {
          console.log("Detailed metrics sync completed successfully!");
        }
      } catch (error) {
        console.error("Error syncing detailed metrics:", error);
      }
    },
    async syncData() {
      try {
        const response = await axios.get(
          `${this.cometPlayerApiUrl}?action=syncData`,
        );

        if (response.data.success) {
          alert("Sync completed successfully!");
          this.loadPlayers();
          this.loadStatistics();
        }
      } catch (error) {
        console.error("Error syncing data:", error);
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-GB");
    },
    getHeadCoachName() {
      const coach = this.selectedCoachesForSelectedTeam.find(
        (coach) => coach.coach_type === "Head",
      );
      return coach ? coach.name : "No coach assigned";
    },
    openAddOppositionTeamModal(team) {
      if (team === undefined) {
        this.selectedOppositionTeam = {};
        this.editMode = false;
      } else {
        this.editMode = true;
        this.selectedOppositionTeam = team;
      }

      this.showAddOppositionTeam = true;
    },
    async deleteOppositionTeam(team) {
      const data = {
        action: "deleteOppositionTeam",
        id: team.id,
      };

      const responce = await Games.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.getAllOppositionTeams();
        this.$toast.success(responce.data);
      }
    },
    async getAllOppositionTeams() {
      const data = {
        action: "getAllOppositionTeams",
      };

      const responce = await Games.find(data).catch((error) => {
        console.log(error);
      });

      this.leagueTeams = responce.data;
    },
    async editOppositionTeam() {
      const data = {
        action: "editOppositionTeam",
        ...this.selectedOppositionTeam,
      };

      const responce = await Games.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.showAddOppositionTeam = false;
        this.getAllOppositionTeams();
        this.$toast.success(responce.data);
      }
    },
    async addOppositionTeam() {
      const data = {
        action: "addOppositionTeam",
        clubId: this.userData.clubId,
        ...this.selectedOppositionTeam,
      };

      const responce = await Games.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.showAddOppositionTeam = false;
        this.getAllOppositionTeams();
        this.$toast.success(responce.data);
      }
    },
    releasePlayerModal(playerData) {
      this.playerToBeReleased = playerData;
      this.showAreYouSureModal = true;
    },
    veiwTeam(team) {
      if (
        this.userData.userType === "Admin" ||
        this.userData.userType === "Coach"
      ) {
        this.$router.push({
          name: "teams",
          params: { teamData: team },
        });
      }
    },
    async archiveUser(userData) {
      userData.userType = "Archived";
      const playerData = {
        ...userData,
        action: "archiveUser",
      };
      const responce = await Users.find(playerData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.teamsAndUserCall();
        this.playerToBeReleased = null;
        this.$toast.success(responce.data);
      }
    },
    async removeCoachFromTeam(coachId) {
      const userData = {
        coach_id: coachId,
        teamId: this.selectedTeam.id,
        action: "removeCoachFromTeam",
      };
      const responce = await Teams.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.getAllCoachesForTeam(this.selectedTeam.id);

        this.$toast.success(responce.data);
      }
    },
    async removePlayerFromTeam(playerId, teamId) {
      const userData = {
        user_id: playerId,
        teamId: teamId,
        action: "removePlayerFromTeam",
      };
      const responce = await Teams.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data === "Player removed from team") {
        this.getPlayersOtherTeams(this.selectedPlayer);
        this.$toast.success(responce.data);
      }
    },
    getImgUrl(pic) {
      return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
    },
    async addAnotherCoachCall(coachId, teamId, coach_type) {
      const userData = {
        coachId: coachId,
        teamId: teamId,
        coach_type: coach_type,
        action: "addCoachToTeam",
      };
      await Teams.find(userData).catch((error) => {
        console.log(error);
      });

      this.changeHeadCoach = false;
      this.changeCoach = false;
      this.getAllCoachesForTeam(teamId);
    },
    updateCurrentSelectedTeamCoachName(
      coachName,
      coachId,
      oldCoachId,
      coach_type,
    ) {
      this.selectedTeam.name = coachName;
      this.selectedTeam.oldCoach_id = oldCoachId;
      this.selectedTeam.coach_id = coachId;
      this.selectedTeam.coach_type = coach_type;
      this.addAnotherCoachCall(coachId, this.selectedTeam.id, coach_type);
    },
    async findCoachByName() {
      if (this.selectedCoach.name !== undefined) {
        const userData = {
          keyword: this.selectedCoach.name,
          clubId: this.userData.clubId,
          action: "findCoachesByName",
        };
        const responce = await Users.find(userData).catch((error) => {
          console.log(error);
        });

        if (responce.data) {
          this.selectedCoachesData = responce.data;
        }
      }
    },
    showCoachMissingValidation(value) {
      this.missingCoachId = value;
    },
    async getAllUsersForCoach() {
      const userData = {
        clubId: this.userData.clubId,
        coach_id: this.userData.id,
        action: "getAllUsersForCoach",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.allPlayersData = responce.data;
      }
    },
    teamsAndUserCall() {
      if (
        this.userData.userType === "Admin" ||
        this.userData.userType === "Coach"
      ) {
        if (this.userData.userType === "Admin") {
          this.getUserByClubId();
          this.getAllStaffByClubId();
        }

        if (this.userData.userType === "Coach") {
          this.getAllUsersForCoach();
        }
        this.getAllteams();
      } else {
        this.selectedNav = "players";
        this.getAllteams();
        this.allPlayersData.push(this.userData);
      }
    },
    async addNewTeam() {
      if (
        Object.prototype.hasOwnProperty.call(this.selectedCoach, "id") === true
      ) {
        this.showCoachMissingValidation(false);
        this.loadingStuff = true;
        this.newTeamData.season_start_date = this.fotmatDatesForDb(
          this.newTeamData.season_start_date,
        );
        this.newTeamData.season_end_date = this.fotmatDatesForDb(
          this.newTeamData.season_end_date,
        );

        const teamData = {
          ...this.newTeamData,
          clubId: this.userData.clubId,
          coach_id: this.selectedCoach.id,
          action: "addTeam",
        };
        const responce = await Teams.find(teamData).catch((error) => {
          console.log(error);
        });

        if (responce.data === "Team added") {
          this.teamsAndUserCall();
          this.showAddNewTeam = false;
          this.loadingStuff = false;
          this.$toast.success("Team added");
        }
      } else {
        this.showCoachMissingValidation(true);
      }
    },
    async upDateTeam() {
      let coachId = null;
      let oldCoachId = null;

      this.loadingStuff = true;
      this.selectedTeam.season_start_date = this.fotmatDatesForDb(
        this.selectedTeam.season_start_date,
      );
      this.selectedTeam.season_end_date = this.fotmatDatesForDb(
        this.selectedTeam.season_end_date,
      );

      if (this.selectedCoach.id === undefined) {
        coachId = this.selectedTeam.coach_id;
        oldCoachId = this.selectedTeam.oldCoach_id;
      }

      const teamData = {
        ...this.selectedTeam,
        coach_id: coachId,
        oldCoach_id: oldCoachId,
        action: "editTeam",
      };
      const responce = await Teams.find(teamData).catch((error) => {
        console.log(error);
      });

      if (responce.data === "Team updated") {
        this.teamsAndUserCall();
        this.showEditTeam = false;
        this.loadingStuff = false;
        this.changeHeadCoach = false;
        this.changeCoach = false;
        this.$toast.success("Team updated");
      }
    },
    clearImageDataFromUpload() {
      ((this.imageURL = ""),
        (this.image = {
          imageName: "",
          path: "",
          file: null,
        }));
    },
    imageUploadObjectDataChanged(data) {
      this.image = data;
    },
    imageUploadFileDataChnged(data) {
      this.imageFile = data;
    },
    populateValidationArray(validationData) {
      return validationData.map((validation) => {
        return validation === true;
      });
    },
    filterValidationResults(validationResults) {
      return validationResults.filter((result) => {
        return result === true;
      });
    },
    togglevalaidationErrors(name, validationState) {
      this.validationValues[name].state = validationState;
    },
    requiredValidation(value) {
      return !value || value.length === 0;
    },
    checkChangeVlaidation(value, nameOfElementToValidate) {
      if (nameOfElementToValidate === "email") {
        const validated = this.requiredEmailValidation(value);
        validated === true
          ? (this.validationValues[nameOfElementToValidate].state = false)
          : (this.validationValues[nameOfElementToValidate].state = true);
      } else {
        this[nameOfElementToValidate].length > 0
          ? (this.validationValues[nameOfElementToValidate].state = false)
          : (this.validationValues[nameOfElementToValidate].state = true);
      }
    },
    setValidationMessage() {
      if (this.needsValidation.length === 0) {
        this.validationMessage = null;
        this.needsValidation = [];
      } else {
        this.validationMessage =
          "Please fix the validation errors before you can proceed.";
        this.isLoading = false;
      }
    },
    reCheckValidation() {
      let validation = this.needsValidation.length === 0;
      validation = this.requiredEmailValidation(this.selectedPlayer.email);
      this.setValidationMessage();
      return validation;
    },
    requiredEmailValidation(value) {
      var validRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      return validRegex.test(value);
    },
    validationCheck() {
      let validationData = [];
      this.needsValidation = [];
      validationData.push(
        this.setUpValidation(this.selectedPlayer.email, "email"),
      );

      let validationResults = this.populateValidationArray(validationData);
      this.needsValidation = this.filterValidationResults(validationResults);
      return this.reCheckValidation();
    },
    setUpValidation(validationValue, validationName) {
      let validationstate = false;
      if (validationName === "email") {
        validationstate = !this.requiredEmailValidation(validationValue);
      } else {
        validationstate = this.requiredValidation(validationValue);
      }
      this.togglevalaidationErrors(validationName, validationstate);
      return validationstate;
    },
    getUserByIdCall(clientId) {
      this.allPlayersData = [];
      this.getUserById(clientId).then((response) => {
        if (response === true) {
          if (
            this.userData.userType === "Admin" ||
            this.userData.userType === "Coach"
          ) {
            if (this.userData.userType === "Admin") {
              this.getUserByClubId();
              this.getAllStaffByClubId();
            }

            if (this.userData.userType === "Coach") {
              this.getAllUsersForCoach();
            }
            this.getAllteams();
          } else {
            this.selectedNav = "players";
            this.getAllteams();
            this.allPlayersData.push(this.userData);
          }
          this.loadingStuff = false;
        }
      });
    },
    changePasswordForFBauth(newPassword) {
      const auth = getAuth();
      const user = auth.currentUser;

      updatePassword(user, newPassword)
        .then(() => {
          console.log("Update successful");
        })
        .catch((error) => {
          console.log("An error ocurred", error);
        });
    },
    async changePassword() {
      if (this.oldPassword === null && this.newPassword === null) {
        return;
      }

      if (this.newPassword.length > 5) {
        this.loadingStuff = true;
        const userData = {
          email: this.userData.email,
          oldPassword: this.oldPassword,
          newPassword: this.newPassword,
          id: this.userData.id,
          action: "passwordReset",
        };
        const responce = await Users.find(userData).catch((error) => {
          console.log(error);
        });

        this.changePasswordForFBauth(this.newPassword);

        if (responce.status === 200) {
          this.loadingStuff = false;
          this.$toast.success("Pasword changed successfully.");
        } else {
          this.loadingStuff = false;
          this.$toast.error("Invalid Username or Password!");
        }
      }
    },
    async addPlayerToNewTeam(event) {
      const userData = {
        user_id: this.selectedPlayer.id,
        teamId: event.target.value,
        action: "addPlayerToNewTeam",
      };

      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.getPlayersOtherTeams(this.selectedPlayer);
        if (responce.data === "User team created") {
          this.$toast.success("Player added to new team");
        }
      }
    },
    async changePlayersTeam(event) {
      this.loadingStuff = true;
      const userData = {
        user_id: this.selectedPlayer.id,
        teamId: event.target.value,
        oldTeamId: this.selectedPlayer.teamId,
        action: "changePlayersTeam",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        if (responce.status === 200) {
          if (this.userData.userType === "Admin") {
            this.getUserByClubId();
            this.getAllStaffByClubId();
          }
          if (this.userData.userType === "Coach") {
            this.getAllUsersForCoach();
            this.getCoachAllDataById();
          }
          if (this.userData.userType === "User") {
            this.getUserByIdCall(this.selectedPlayer.id);
          }
          this.showTeamsSelection = false;
          this.selectedPlayer.teamId = Number(event.target.value);
          this.selectedPlayerTeamValue = null;
          this.loadingStuff = false;
          this.$toast.success("Users team changed");
        }
      }
    },
    getTeamNameFromId(id) {
      if (id === "") {
        id = undefined;
      }
      let arrayResult = null;
      if (this.teams.length > 0 && id !== undefined) {
        arrayResult = this.teams.filter((team) => {
          return team.id == id;
        });
      }
      if (arrayResult !== null && arrayResult.length > 0) {
        return arrayResult !== null ? arrayResult[0].teamName : "";
      }
    },
    editUserModalClose() {
      this.selectedPlayer = {};
      this.loadingStuff = false;
    },
    resetPlayersOtherTeamsData() {
      this.playersOtherTeamsData = [];
      this.showTeamsSelection = false;
      this.showTeamsSelectionAddTeam = false;
    },
    async getPlayersOtherTeams(playerData) {
      this.resetPlayersOtherTeamsData();
      const userData = {
        defaultTeamId: playerData.teamId,
        user_id: playerData.id,
        action: "getPlayersOtherTeams",
      };
      const responce = await Teams.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.playersOtherTeamsData = responce.data;
      }
    },
    openEditUserModal(playerData) {
      this.loadingStuff = true;
      this.selectedPlayer = { ...playerData };
      this.getPlayersOtherTeams(playerData);
      this.showEditUser = true;
      this.loadingStuff = false;
    },
    resetSelectedCoachata() {
      this.changeCoach = false;
      this.selectedCoachesData = [];
      this.selectedCoach = {};
    },
    resetNewTeamData() {
      // Pre-fill season dates with the tenant's current season window
      // (UK: Jul-Jun; AU calendar tenants: Jan-Dec) — admins can override.
      const season = getSeasonWindow();
      this.newTeamData = {
        teamName: "",
        leagueName: "",
        clubId: "",
        league_link: "",
        season_start_date: season.startDate,
        season_end_date: season.endDate,
        age_group: "0000",
      };
    },
    openAddNewTeamModal() {
      this.resetNewTeamData();
      this.resetSelectedCoachata();
      this.showAddNewTeam = true;
    },
    openEditTeamModal(teamData) {
      this.resetSelectedCoachata();
      this.getAllCoachesForTeam(teamData.teamId);
      this.selectedTeam = { ...teamData };
      this.showEditTeam = true;
    },
    openSeasonDatesEditor(teamData) {
      this.selectedTeamForSeasonEdit = { ...teamData };
      this.showSeasonDatesEditor = true;
    },
    closeSeasonDatesEditor() {
      this.showSeasonDatesEditor = false;
      this.selectedTeamForSeasonEdit = null;
    },
    onSeasonDatesSaved() {
      this.teamsAndUserCall();
    },
    async getAllCoachesForTeam(teamId) {
      const teamData = {
        teamId: teamId,
        action: "getAllCoachesForTeam",
      };

      const responce = await Teams.find(teamData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.selectedCoachesForSelectedTeam = responce.data;
      }
    },
    showModal(modalName) {
      // Legacy method - now uses direct property assignment
      switch (modalName) {
        case "editTeam":
          this.showEditTeam = true;
          break;
        case "addNewTeam":
          this.showAddNewTeam = true;
          break;
        case "addOppositionTeam":
          this.showAddOppositionTeam = true;
          break;
        case "areYouSureModal":
          this.showAreYouSureModal = true;
          break;
        case "editUser":
          this.showEditUser = true;
          break;
      }
    },
    hideModal(modalName) {
      // New method for hiding modals
      switch (modalName) {
        case "editTeam":
          this.showEditTeam = false;
          break;
        case "addNewTeam":
          this.showAddNewTeam = false;
          break;
        case "addOppositionTeam":
          this.showAddOppositionTeam = false;
          break;
        case "areYouSureModal":
          this.showAreYouSureModal = false;
          break;
        case "editUser":
          this.showEditUser = false;
          break;
      }
    },
    async getAllStaffByClubId() {
      const userData = {
        clubId: this.userData.clubId,
        action: "getAllStaffByClubId",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.allStaffData = responce.data;
      }
    },
    openAddStaffModal() {
      this.newStaff = {
        name: "",
        email: "",
        userType: "Coach",
        position: "",
        phone: "",
        teamId: 0,
      };
      this.addStaffError = null;
      this.addStaffResult = null;
      this.showAddStaff = true;
      if (!this.teams.length) {
        this.getTeamsByCoachid();
      }
    },
    async submitAddStaff() {
      this.addStaffError = null;
      if (!this.newStaff.name.trim() || !this.newStaff.email.trim()) {
        this.addStaffError = "Name and email are required.";
        return;
      }
      this.addingStaff = true;
      const payload = {
        action: "addStaff",
        name: this.newStaff.name.trim(),
        email: this.newStaff.email.trim(),
        userType: this.newStaff.userType,
        position: this.newStaff.position,
        phone: this.newStaff.phone,
        teamId: this.newStaff.teamId || 0,
        clubId: this.userData.clubId,
      };
      // Read error.response so the backend's specific message (duplicate email,
      // validation) surfaces on 4xx/5xx, not just a generic failure.
      const res = await Users.find(payload).catch((e) =>
        e && e.response ? e.response : null,
      );
      this.addingStaff = false;
      const data = res && res.data;
      if (data && data.success) {
        this.addStaffResult = data;
        this.getAllStaffByClubId();
      } else {
        this.addStaffError =
          (data && data.error) ||
          "Could not add the staff member. Please try again.";
      }
    },
    closeAddStaffModal() {
      this.showAddStaff = false;
      this.addStaffResult = null;
    },
    async getCoachAllDataById() {
      const userData = {
        coachId: this.userData.id,
        action: "getCoachAllDataById",
      };

      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        if (this.userData.userType === "Admin") {
          this.adminUserData = responce.data;
        } else {
          this.allStaffData = responce.data;
        }
      }
    },
    async getAllArchivedUsersByClubId() {
      const userData = {
        clubId: this.userData.clubId,
        action: "getAllArchivedUsersByClubId",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.allArchivedPlayersData = responce.data;
      }
    },
    async getUserByClubId() {
      const userData = {
        clubId: this.userData.clubId,
        action: "getUserByClubId",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.allPlayersData = responce.data;
        this.getAllArchivedUsersByClubId();
      }
    },
    async getUserById(clientId) {
      console.log(clientId);
      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.userData = responce.data[0];
        const userData = responce.data[0];
        this.$store.dispatch(USER_POPULATE, { userData });
        if (
          this.userData.userType === "Admin" ||
          this.userData.userType === "Coach"
        ) {
          this.getAllUsersForThisAdmin();
        }
        return true;
      }
    },
    setUser() {
      if (this.getProfile !== null) {
        this.userData = this.getProfile;
      }
    },
    async upDatePlayerProfile() {
      let selectedPlayerId = this.selectedPlayer.id;
      if (
        this.validationCheck() === true &&
        this.selectedPlayer.joined_date !== null &&
        this.selectedPlayer.dob !== null &&
        this.selectedPlayer.dob !== "Invalid date" &&
        this.selectedPlayer.joined_date !== "Invalid date"
      ) {
        this.showEditUser = false;
        this.loadingStuff = true;

        this.selectedPlayer.dob !== "0000-00-00"
          ? (this.selectedPlayer.dob = this.fotmatDatesForDb(
              this.selectedPlayer.dob,
            ))
          : (this.selectedPlayer.dob = this.fotmatDatesForDb(moment()));

        this.selectedPlayer.joined_date !== "0000-00-00"
          ? (this.selectedPlayer.joined_date = this.fotmatDatesForDb(
              this.selectedPlayer.joined_date,
            ))
          : (this.selectedPlayer.joined_date = this.fotmatDatesForDb(moment()));

        if (this.image.imageFile !== null && this.imageFile !== null) {
          this.selectedPlayer.fileName = this.imageFile.imageName;
          this.selectedPlayer.imageFile = this.image;
        }
        if (this.selectedPlayer.userType !== "User") {
          this.selectedPlayer.trialistId = null;
          this.selectedPlayer.place_of_birth = "";
          this.selectedPlayer.country_of_birth = "";
          this.selectedPlayer.nationality = "";
          this.selectedPlayer.previous_club = "";
          this.selectedPlayer.parent_name = "";

          if (this.selectedPlayer.post_code === undefined) {
            this.selectedPlayer.post_code = "";
          }
        }
        const userData = {
          ...this.selectedPlayer,
          action: "upDatePlayerProfile",
        };
        const responce = await Users.find(userData).catch((error) => {
          console.log(error);
        });

        if (responce.data == 1) {
          this.getUsersByUserType(selectedPlayerId);
          this.loadingStuff = false;
          this.$toast.success("Profile updated.");
        }
      }
    },
    getUsersByUserType(selectedPlayerId) {
      if (this.userData.userType === "User") {
        this.getUserByIdCall(selectedPlayerId);
      }
      if (this.userData.userType === "Admin") {
        this.getUserByClubId();
        this.getAllStaffByClubId();
      }
      if (this.userData.userType === "Coach") {
        this.getAllUsersForCoach();
        this.getAllStaffByClubId();
      }
    },
    async getAllUsersForThisAdmin() {
      const userData = {
        coachId: this.userData.id,
        action: "getUserByCoachId",
      };
      await Users.find(userData).catch((error) => {
        console.log(error);
      });
    },
    formatDates(date) {
      return moment(date).format("DD MMM YYYY");
    },
    fotmatDatesForDb(date) {
      console.log(date);
      return date === null ? null : moment(date).format("YYYY-MM-DD");
    },
    changeSubNav(navItem) {
      this.selectedNav = navItem;

      if (navItem === "staff") {
        this.getCoachAllDataById();
      }
    },
    async getTeamsByCoachid() {
      const data = {
        action: "getTeamsByCoachid",
        coach_id: this.userData.id,
      };

      const responce = await Teams.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.teams = responce.data;
      }
    },
    async getAllteams() {
      if (this.userData.userType === "Coach") {
        this.getTeamsByCoachid();
      } else {
        const userData = {
          clubId: this.userData.clubId,
          action: "populateTeamData",
        };
        const responce = await Teams.find(userData).catch((error) => {
          console.log(error);
        });
        if (responce.data) {
          this.teams = responce.data;
        }
      }
    },
    removeDuplicates(dataArray) {
      return dataArray.filter((team) => team.coach_type === "Head");
      // [...new Map(dataArray.map(item => [item[key], item])).values()]
    },
  },
};
</script>
<style scoped lang="scss">
/* Section side-drawer layout (SectionNav). Fixed left rail; settings content
   padded left to clear it. Mirrors the Profile/Teams page pattern. */
.settings-page.with-drawer {
  --section-nav-w: 260px;
  padding-left: calc(var(--section-nav-w) + 28px);
  transition: padding-left 0.2s ease;
}

.settings-page.with-drawer.nav-collapsed {
  --section-nav-w: 72px;
}

@media (max-width: 768px) {
  .settings-page.with-drawer,
  .settings-page.with-drawer.nav-collapsed {
    padding-left: 0;
  }
}

.settings-body {
  margin-top: 2rem;
  background: #fff;
  padding: 1rem;
}

label.form__label {
  width: 11rem;
}

input#age_group {
  width: 6rem;
}

.training-plan-display {
  width: 100%;
  display: block;
  border: 1px solid #dddddd;
  padding: 1rem;
  margin: 1rem 0;
  max-height: 10rem;
  overflow-y: scroll;
  background: #f9f9f9;

  .selected-training-plan-box {
    border: 1px solid #06569e;
    padding: 1rem;
  }
}

span.current-coach-change-link {
  margin-right: 0.5rem;
  font-weight: bold;
}

img.user-image-small {
  width: 4rem;
  height: 4rem;
  display: block;
  margin: 0 auto;
  border-radius: 400px;
}

.more-team-information {
  margin-top: 1rem;

  h3 {
    font-size: 1rem;
    text-transform: inherit;
  }

  .extra-teams-list {
    border: 1px solid #dddddd;
    padding: 1rem;
    overflow-y: scroll;
    height: 10rem;
    background: #f1f4f8;

    p {
      border: 1px solid #95dab6;
      margin: 0;
      padding: 0.5rem;
      border-radius: 10px;
      margin-bottom: 0.2rem;
      background: #edfef5;
    }
  }
}

.add-other-team-button {
  margin-top: 0.5rem;
  display: block;
}

.coach-header {
  margin-bottom: 0;
  margin-top: 0.5rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

/* Notification Settings Styles */
.notification-settings-content {
  max-width: 500px;
}

.notification-info-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: #f5f5f7;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.notification-info-icon {
  flex-shrink: 0;
}

.notification-info-text {
  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
    color: #1d1d1f;
  }

  p {
    margin: 0;
    color: #6e6e73;
    font-size: 0.95rem;
    line-height: 1.4;
  }
}

.notification-reset-section,
.notification-status-section {
  padding: 1rem;
  background: #ffffff;
  border: 1px solid #e5e5e7;
  border-radius: 10px;
}

.notification-status {
  margin: 0 0 0.75rem 0;
}

.status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-dismissed {
  background: #fef3cd;
  color: #856404;
}

.status-pending {
  background: #e8f4fd;
  color: #0066cc;
}

.notification-help-text {
  color: #6e6e73;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.override-pill {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 8px;
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffe5a0;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 500;
  vertical-align: middle;
}

.inherits-text {
  color: #adb5bd;
}
</style>
